/**
 * AI Import Service
 * Service layer for the Claude-powered file ingestion pipeline.
 *
 * Edge Function endpoints (submit / apply) are called via fetch with the
 * anon key; jobs/files/questions/feedback CRUD goes through Supabase REST.
 */

import { supabase } from '@/lib/supabase';
import { restHeaders } from '@/lib/supabaseRest';
import { OVERRIDABLE_LEAD_TIERS, readRetryResponse, type FilingRetryResult } from '@/lib/openLaneUi';
import type { PassReason } from '../../supabase/functions/_shared/broker-reply';
import { RERUN_FILE_COLUMNS, rerunnableFiles } from '@/lib/aiImportRerun';
import type {
  AIImportJob,
  AIImportJobUpdate,
  AIImportJobFile,
  AIImportJobFileFiling,
  AIImportQuestion,
  AIImportQuestionStatus,
  AIImportEmailMessage,
  AIImportFeedback,
  AIImportFeedbackInsert,
  AIImportJobFilters,
  AIImportPlan,
  ListingCandidate,
  OpenLaneLead,
} from '@/types/ai-import';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

// Cap the imports list. The email pipeline creates jobs continuously, so an
// unbounded select=* (with the large summary/report jsonb) degrades over time.
const JOB_LIST_LIMIT = 50;

function buildJobFilterQuery(filters?: AIImportJobFilters): string {
  const params = new URLSearchParams();
  params.set('select', '*');
  params.set('order', 'created_at.desc');
  params.set('limit', String(JOB_LIST_LIMIT));

  if (filters?.status) {
    const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
    params.set('status', `in.(${statuses.join(',')})`);
  }
  if (filters?.source) {
    params.set('source', `eq.${filters.source}`);
  }
  if (filters?.intake_lane) {
    params.set('intake_lane', `eq.${filters.intake_lane}`);
  }
  if (filters?.submitted_by) {
    params.set('submitted_by', `eq.${filters.submitted_by}`);
  }
  if (filters?.search) {
    // Search server-side across subject + submitter (case-insensitive) so a
    // keystroke doesn't pull the whole table down to filter client-side. Strip
    // PostgREST filter-grammar characters so a stray comma/paren can't break it.
    const q = filters.search.replace(/[,()*\\]/g, ' ').trim();
    if (q) {
      params.set('or', `(source_email_subject.ilike.*${q}*,submitted_by.ilike.*${q}*)`);
    }
  }
  return params.toString();
}

// ============================================================================
// JOBS
// ============================================================================

export async function getJobs(filters?: AIImportJobFilters): Promise<AIImportJob[]> {
  const query = buildJobFilterQuery(filters);
  const response = await fetch(`${SUPABASE_URL}/rest/v1/ai_import_jobs?${query}`, {
    headers: await restHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch ai_import_jobs: ${response.status}`);
  }
  return response.json();
}

export async function getJob(id: string): Promise<AIImportJob | null> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_jobs?id=eq.${id}&select=*`,
    { headers: await restHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch ai_import_job ${id}: ${response.status}`);
  }
  const rows: AIImportJob[] = await response.json();
  return rows[0] ?? null;
}

export async function updateJob(id: string, update: AIImportJobUpdate): Promise<AIImportJob> {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/ai_import_jobs?id=eq.${id}`, {
    method: 'PATCH',
    headers: await restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(update),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to update ai_import_job ${id}: ${response.status} ${text}`);
  }
  const rows: AIImportJob[] = await response.json();
  return rows[0];
}

/**
 * Dismisses one broker question (the card's Dismiss): send-ai-import-report's dismiss_broker_question, which checks the
 * session (an allowlisted teammate) and writes through updateBrokerQuestions, the one writer of the list (a
 * compare-and-set, so a reply landing at the same moment is not overwritten). `expectedText` (the text the card showed)
 * keeps a list that moved under the page from dismissing a different question than the one clicked. Its refusals (409
 * the list changed or the broker answered, 401/403 not a teammate) arrive as the error text the function wrote.
 */
export async function dismissBrokerQuestion(jobId: string, index: number, expectedText: string): Promise<void> {
  const { error } = await supabase.functions.invoke('send-ai-import-report', {
    body: { job_id: jobId, action: 'dismiss_broker_question', index, expected_text: expectedText },
  });
  if (error) throw await describeFunctionError(error, 'send-ai-import-report');
}

export async function updateJobSummary(id: string, summary: AIImportPlan): Promise<AIImportJob> {
  return updateJob(id, { summary });
}

export async function cancelJob(id: string): Promise<AIImportJob> {
  return updateJob(id, { status: 'cancelled' });
}

// ============================================================================
// JOB FILES
// ============================================================================

/**
 * Import source files applied to a property (Documents hub "From AI imports").
 * Files whose apply created a documents row (apply_document_id set) are
 * excluded — they already render in the property-documents section.
 */
export async function getJobFilesByProperty(propertyId: string): Promise<AIImportJobFile[]> {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_job_files?apply_property_id=eq.${propertyId}&apply_document_id=is.null&order=created_at.desc&select=*`,
    { headers: await restHeaders() },
  );
  if (!res.ok) throw new Error(`getJobFilesByProperty failed: ${res.status}`);
  return res.json();
}

export async function getJobFiles(jobId: string): Promise<AIImportJobFile[]> {
  // file_name/id break the created_at tie: a job's rows are inserted in ONE
  // statement and therefore share a created_at to the microsecond, so ordering
  // on it alone leaves Postgres free to return them in physical order — which
  // shifts under the worker's own UPDATEs and makes the polled file list
  // reshuffle on every refetch.
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_job_files?job_id=eq.${jobId}&order=created_at.asc,file_name.asc,id.asc&select=*`,
    { headers: await restHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch job files for ${jobId}: ${response.status}`);
  }
  return response.json();
}

// ============================================================================
// EMAIL HISTORY
// ============================================================================

export async function getJobEmails(jobId: string): Promise<AIImportEmailMessage[]> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_email_messages?job_id=eq.${jobId}&order=occurred_at.asc,created_at.asc&select=*`,
    { headers: await restHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch email history for ${jobId}: ${response.status}`);
  }
  return response.json();
}

// ============================================================================
// QUESTIONS
// ============================================================================

export async function getJobQuestions(jobId: string): Promise<AIImportQuestion[]> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_questions?job_id=eq.${jobId}&order=created_at.asc&select=*`,
    { headers: await restHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch questions for ${jobId}: ${response.status}`);
  }
  return response.json();
}

export async function resolveQuestion(
  questionId: string,
  payload: {
    status: AIImportQuestionStatus;
    resolved_value?: unknown;
    resolved_by: string;
  },
): Promise<AIImportQuestion> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_questions?id=eq.${questionId}`,
    {
      method: 'PATCH',
      headers: await restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify({
        status: payload.status,
        resolved_value: payload.resolved_value ?? null,
        resolved_by: payload.resolved_by,
        resolved_at: new Date().toISOString(),
      }),
    },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to resolve question ${questionId}: ${response.status} ${text}`);
  }
  const rows: AIImportQuestion[] = await response.json();
  return rows[0];
}

// ============================================================================
// FEEDBACK (learning loop)
// ============================================================================

export async function recordFeedback(insert: AIImportFeedbackInsert): Promise<AIImportFeedback> {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/ai_import_feedback`, {
    method: 'POST',
    headers: await restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(insert),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to record ai_import_feedback: ${response.status} ${text}`);
  }
  const rows: AIImportFeedback[] = await response.json();
  return rows[0];
}

// ============================================================================
// ANALYTICS — aggregate "what AI derived" across all jobs
// ============================================================================

export interface AIImportAnalytics {
  totalJobs: number;
  appliedJobs: number;
  pendingReview: number;
  propertiesCreated: number;
  propertiesUpdated: number;
  /** Properties updated specifically by enrichment jobs (listing digests + single-asset forwards). */
  propertiesEnriched: number;
  /** Count of applied enrichment jobs. */
  enrichmentJobs: number;
  filesProcessed: number;
  salesComps: number;
  refiComps: number;
  appraisals: number;
  rentComps: number;
  valuationsTotal: number;
  totalCostUsd: number;
}

/**
 * Count rows matching a PostgREST filter without transferring them, using the
 * Content-Range header (Prefer: count=exact + a zero-width range).
 */
async function countRows(table: string, filter: string): Promise<number> {
  const sep = filter ? '&' : '';
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=id${sep}${filter}`, {
    headers: await restHeaders({ Prefer: 'count=exact', Range: '0-0' }),
  });
  if (!res.ok) return 0;
  const range = res.headers.get('content-range'); // e.g. "0-0/137" or "*/0"
  const total = range?.split('/')?.[1];
  const n = total ? Number(total) : 0;
  return Number.isFinite(n) ? n : 0;
}

export async function getAIImportAnalytics(): Promise<AIImportAnalytics> {
  // Jobs: sum counters client-side (job count is small).
  const jobsRes = await fetch(
    // job_kind is read from the summary jsonb (alias) so enrichment jobs can be
    // counted separately without transferring the whole plan.
    `${SUPABASE_URL}/rest/v1/ai_import_jobs?select=status,properties_created,properties_updated,files_count,cost_usd,job_kind:summary->>job_kind`,
    { headers: await restHeaders() },
  );
  if (!jobsRes.ok) throw new Error(`Failed to fetch jobs analytics: ${jobsRes.status}`);
  const jobs: Array<{
    status: string;
    properties_created: number | null;
    properties_updated: number | null;
    files_count: number | null;
    cost_usd: number | null;
    job_kind: string | null;
  }> = await jobsRes.json();

  let propertiesCreated = 0;
  let propertiesUpdated = 0;
  let propertiesEnriched = 0;
  let enrichmentJobs = 0;
  let filesProcessed = 0;
  let totalCostUsd = 0;
  let appliedJobs = 0;
  let pendingReview = 0;
  for (const j of jobs) {
    const isEnrichment = j.job_kind === 'enrichment';
    if (j.status === 'applied') {
      propertiesCreated += j.properties_created ?? 0;
      // Enrichment jobs also record their updates in properties_updated; count
      // them under their own metric so both are visible on the dashboard.
      if (isEnrichment) {
        propertiesEnriched += j.properties_updated ?? 0;
        enrichmentJobs += 1;
      } else {
        propertiesUpdated += j.properties_updated ?? 0;
      }
      appliedJobs += 1;
    }
    if (j.status === 'completed_pending_review') pendingReview += 1;
    if (j.status !== 'cancelled') filesProcessed += j.files_count ?? 0;
    totalCostUsd += j.cost_usd ?? 0;
  }

  // Comps derived by AI. Valuations are tagged data_source='ai_import:{job}';
  // rent comps carry ai_import_job_id. Count by type via Content-Range headers.
  const [salesComps, refiComps, appraisals, rentComps] = await Promise.all([
    countRows('property_valuations', 'data_source=like.ai_import*&transaction_type=eq.sale'),
    countRows('property_valuations', 'data_source=like.ai_import*&transaction_type=eq.refinance'),
    countRows('property_valuations', 'data_source=like.ai_import*&transaction_type=eq.appraisal'),
    countRows('market_rent_comps', 'ai_import_job_id=not.is.null'),
  ]);

  return {
    totalJobs: jobs.length,
    appliedJobs,
    pendingReview,
    propertiesCreated,
    propertiesUpdated,
    propertiesEnriched,
    enrichmentJobs,
    filesProcessed,
    salesComps,
    refiComps,
    appraisals,
    rentComps,
    valuationsTotal: salesComps + refiComps + appraisals,
    totalCostUsd,
  };
}

// ============================================================================
// EDGE FUNCTION ENTRY POINTS
// These call the server-side Claude pipeline.
// The Edge Functions themselves are built in Phase 2; the client calls live
// here so wiring is centralized and easy to swap.
// ============================================================================

export interface SubmitJobPayload {
  source: 'upload' | 'email' | 'rerun';
  submitted_by: string;
  submitted_by_user_id?: string | null;
  submitted_by_email?: string | null;
  import_mode?: 'deal' | 'database';
  mode_directive?: 'enrich' | 'analyze' | 'both';
  files: Array<{
    file_name: string;
    file_size?: number;
    file_type?: string;
    file_extension?: string;
    sharepoint_item_id?: string;
    sharepoint_drive_id?: string;
    sharepoint_web_url?: string;
    /** A Storage object is named by bucket AND path; the worker opens nothing with only one of them. */
    storage_bucket?: string;
    storage_path?: string;
    content_id?: string;
    source: 'attachment' | 'inline_link' | 'dashboard_upload';
    source_url?: string;
  }>;
  user_hint?: string;
  /**
   * Re-run provenance: the job these files were re-submitted from. The submit
   * function stamps it into parent_job_id and carries that job's acknowledged
   * question rows onto the new job (see carryAckedQuestions in the edge
   * functions' _shared/ai-import-files.ts).
   */
  rerun_of_job_id?: string;
}

/**
 * supabase-js reports any Edge Function failure with the same opaque message
 * ("Edge Function returned a non-2xx status code"). The real reason is in the
 * Response body (carried on `error.context`), e.g. our functions return
 * `{ error: "file X is too large (110 MB > 100 MB)" }`. Pull that out so the UI
 * shows something the user can act on.
 */
async function describeFunctionError(error: unknown, fnName: string): Promise<Error> {
  const ctx = (error as { context?: unknown })?.context;
  if (ctx instanceof Response) {
    try {
      const body = await ctx.clone().json();
      const msg = (body?.error || body?.message) as string | undefined;
      if (msg) {
        const details =
          body?.details != null
            ? ` (${typeof body.details === 'string' ? body.details : JSON.stringify(body.details)})`
            : '';
        return new Error(`${msg}${details}`);
      }
    } catch {
      try {
        const text = await ctx.clone().text();
        if (text) return new Error(text);
      } catch {
        /* fall through to the generic error below */
      }
    }
  }
  return error instanceof Error ? error : new Error(`${fnName} failed: ${String(error)}`);
}

export async function submitJob(
  payload: SubmitJobPayload,
): Promise<{ job_id: string; questions_carried?: number }> {
  const { data, error } = await supabase.functions.invoke('ai-import-submit', {
    body: payload,
  });
  if (error) throw await describeFunctionError(error, 'ai-import-submit');
  return data as { job_id: string; questions_carried?: number };
}

/**
 * Result of applying an import. `property_ids` are newly created properties and
 * `updated_property_ids` are matched (updated) ones — surfaced so the caller can
 * recompute + persist star ratings by id through the trusted path (Task 9).
 */
export interface ApplyJobResult {
  import_mode?: 'deal' | 'database';
  deal_id: string | null;
  property_ids?: string[];
  updated_property_ids?: string[];
  properties_created?: number;
  properties_updated?: number;
  documents_added?: number;
  valuations_added?: number;
  rent_comps_added?: number;
  financial_model_created?: boolean;
  /** Deal mode: standard checklist rows seeded for the primary park (0 when the seed failed, or in database mode). */
  diligence_items_seeded?: number;
  /** Why the checklist was not seeded — Postgres code and message; null when it was, or in database mode. */
  diligence_seed_error?: string | null;
  sources_created?: number;
  contacts_created?: number;
}

export async function applyJob(
  jobId: string,
  applied_by: string,
  applied_by_email: string | null,
): Promise<ApplyJobResult> {
  const { data, error } = await supabase.functions.invoke('ai-import-apply', {
    body: { job_id: jobId, applied_by, applied_by_email },
  });
  if (error) throw await describeFunctionError(error, 'ai-import-apply');
  return data as ApplyJobResult;
}

export type UnderwritingScenario = 'conservative' | 'base' | 'aggressive';

/**
 * Regenerate the persisted analysis (report_analysis) for the SAME job under a
 * chosen underwriting scenario — non-destructive: no email is sent and no new
 * job is created (unlike rerunAIImportJob). Reuses the report function's
 * generate_only path; the job query then repolls in the new analysis.
 */
export async function regenerateAnalysis(
  jobId: string,
  scenario: UnderwritingScenario,
  scenarioOnly = true,
): Promise<void> {
  // scenarioOnly (the in-app toggle) recomputes ONLY the numbers, preserving the
  // narrative/badge. A self-heal of a never-generated analysis passes false to
  // regenerate the full report.
  const { error } = await supabase.functions.invoke('send-ai-import-report', {
    body: { job_id: jobId, generate_only: true, scenario_only: scenarioOnly, assumptions: { scenario } },
  });
  if (error) throw await describeFunctionError(error, 'send-ai-import-report');
}

export async function rollbackJob(
  jobId: string,
  rolled_back_by: string,
  rolled_back_by_email: string | null,
): Promise<void> {
  const { error } = await supabase.functions.invoke('ai-import-rollback', {
    body: { job_id: jobId, rolled_back_by, rolled_back_by_email },
  });
  if (error) throw await describeFunctionError(error, 'ai-import-rollback');
}

/**
 * Re-run a job's AI extraction by creating a NEW import job from the same
 * staged files (mirrors the detail page's "Re-run with AI" action, so it can be
 * triggered from the list without opening the job). Returns the new job id.
 * Throws if the job has no re-runnable files.
 */
export async function rerunAIImportJob(args: {
  jobId: string;
  userName: string;
  userEmail: string | null;
}): Promise<{ job_id: string; questions_carried?: number }> {
  // Narrow projection on purpose: select=* would drag every file's
  // extracted_data blob (the largest column in the schema) over the wire just
  // to rebuild submit metadata.
  const [old, { data: files, error: filesError }] = await Promise.all([
    getJob(args.jobId),
    supabase.from('ai_import_job_files').select(RERUN_FILE_COLUMNS).eq('job_id', args.jobId),
  ]);
  if (filesError) throw filesError;
  // A row is re-runnable only when the worker could open it: SharePoint ids, a
  // SharePoint URL, or a Storage object named by bucket AND path. The bucket was
  // dropped here until 2026-10-09, which is why every seller-financials re-run
  // failed in the worker (src/lib/aiImportRerun.ts).
  const usable = rerunnableFiles(files ?? []);
  if (!usable.length) throw new Error('This import has no re-runnable files.');
  return submitJob({
    source: 'rerun',
    submitted_by: args.userName,
    submitted_by_email: args.userEmail,
    files: usable,
    // A re-run is the SAME work retried: keep the old job's chosen mode —
    // otherwise an email job in database mode re-runs as 'deal' and apply
    // creates a deal the user deliberately never opened.
    import_mode: old?.summary?.import_mode ?? undefined,
    mode_directive: old?.summary?.mode_directive ?? undefined,
    // Carry question memory: without this, every question the user already
    // answered or dismissed on the old job re-asks on the new one.
    rerun_of_job_id: args.jobId,
  });
}

// ============================================================================
// EMAIL INTAKE V2 — filings and the open lane (migration 260)
// ============================================================================

/** Ids per `in.(…)` filter, so a big job's URL stays a sane length (ai-import-file batches its own reads alike). */
const FILINGS_ID_BATCH = 100;

/**
 * Where each job file was filed under a property (RLS: allowlisted users read; only ai-import-file writes).
 * Read in batches of 100 ids, in parallel, and merged.
 */
export async function getFilingsForFiles(fileIds: string[]): Promise<AIImportJobFileFiling[]> {
  if (!fileIds.length) return [];
  const headers = await restHeaders();
  const batches: string[][] = [];
  for (let i = 0; i < fileIds.length; i += FILINGS_ID_BATCH) batches.push(fileIds.slice(i, i + FILINGS_ID_BATCH));
  const pages = await Promise.all(
    batches.map(async (ids) => {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/ai_import_job_file_filings?job_file_id=in.(${ids.join(',')})&order=created_at.asc,id.asc&select=*`,
        { headers },
      );
      if (!response.ok) throw new Error(`Failed to fetch filings: ${response.status}`);
      return (await response.json()) as AIImportJobFileFiling[];
    }),
  );
  return pages.flat();
}

/**
 * Retry filing: ai-import-file's `retry` action re-queues the job's failed rows (and, for an applied job, the
 * files that never got one), then drains. The answer is read defensively: its numbers may be absent.
 */
export async function retryFiling(jobId: string): Promise<FilingRetryResult> {
  const { data, error } = await supabase.functions.invoke('ai-import-file', {
    body: { job_id: jobId, action: 'retry' },
  });
  if (error) throw await describeFunctionError(error, 'ai-import-file');
  return readRetryResponse(data);
}

/**
 * Send the branded decline to an open-lane job's sender: a teammate's decision, checked and sent by
 * send-ai-import-report. Its refusals (400 wrong kind of job, 409 already declined, mail it will not answer)
 * arrive as the error text the function wrote.
 */
export async function sendOpenLaneDecline(jobId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('send-ai-import-report', {
    body: { job_id: jobId, action: 'send_decline' },
  });
  if (error) throw await describeFunctionError(error, 'send-ai-import-report');
}

/**
 * Send the broker of a teammate's forward a pass (spec §E): checked and sent by send-ai-import-report from the signed-in
 * teammate's name. Its refusals (400 a job that cannot take one or a bad reason, 409 a reply already went or mail it
 * will not send) arrive as the error text the function wrote.
 */
export async function sendBrokerPass(jobId: string, reason: PassReason, note?: string | null): Promise<void> {
  const { error } = await supabase.functions.invoke('send-ai-import-report', {
    body: { job_id: jobId, action: 'send_pass', reason, ...(reason === 'other' ? { note: note ?? '' } : {}) },
  });
  if (error) throw await describeFunctionError(error, 'send-ai-import-report');
}

/** Send the broker what the team needs to underwrite the deal (spec §E): the needs-info, as sendBrokerPass. */
export async function sendBrokerNeedsInfo(jobId: string, items: string[]): Promise<void> {
  const { error } = await supabase.functions.invoke('send-ai-import-report', {
    body: { job_id: jobId, action: 'send_needs_info', items },
  });
  if (error) throw await describeFunctionError(error, 'send-ai-import-report');
}

/**
 * The open lane's audit trail: inbound_leads rows with an open_* tier, newest first. `like.open_*` is the
 * partial index's own predicate (WHERE lead_tier LIKE 'open_%'), so this stays an index read.
 */
export async function getOpenLaneLeads(limit = 50): Promise<OpenLaneLead[]> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/inbound_leads?lead_tier=like.open_*&order=created_at.desc&limit=${limit}&select=id,sender_email,sender_name,subject,received_at,lead_tier,triage,header_facts,ai_import_job_id,decline_sent_at,override_requested_at,reply_to_decline`,
    { headers: await restHeaders() },
  );
  if (!response.ok) throw new Error(`Failed to fetch open-lane leads: ${response.status}`);
  return response.json();
}

/**
 * The lead of a job's inbound message that carries a decline stamp (`decline_sent_at`): what was already sent its
 * sender. Send decline needs it: the automatic decline stamps the lead, and "Treat as property" on that lead makes
 * a new job of the same message, which must not offer a decline the server refuses as already sent. RLS:
 * allowlisted users read inbound_leads.
 *
 * The stamp itself is asked for (zero or one row, the latest), not "the job's first few leads": every reply on an
 * open-lane job records a lead of its own, so a job can hold many, and an unordered read of five can miss the one
 * that holds the stamp. An empty answer means no lead of the job was declined; the job's own mail (an outbound
 * `decline`) is checked beside it, in declineAlreadySent.
 */
export async function getLeadsForJob(jobId: string): Promise<Array<Pick<OpenLaneLead, 'id' | 'decline_sent_at'>>> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/inbound_leads?ai_import_job_id=eq.${encodeURIComponent(jobId)}&decline_sent_at=not.is.null&order=decline_sent_at.desc&select=id,decline_sent_at&limit=1`,
    { headers: await restHeaders() },
  );
  if (!response.ok) throw new Error(`Failed to fetch the lead of job ${jobId}: ${response.status}`);
  return response.json();
}

/**
 * "Treat as property": the poller's next pass re-fetches the message by its Graph id and runs the open lane on
 * it without triage. The write is conditional on what that pass picks up (a declined, capped or dropped lead
 * with no request pending), so a list that has gone stale cannot leave a request behind that nothing will ever
 * read, and a second click cannot move the first one's timestamp.
 */
export async function requestOpenLaneOverride(leadId: string): Promise<void> {
  const { data, error } = await supabase
    .from('inbound_leads')
    .update({ override_requested_at: new Date().toISOString() })
    .eq('id', leadId)
    .in('lead_tier', [...OVERRIDABLE_LEAD_TIERS])
    .is('override_requested_at', null)
    .select('id');
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) {
    throw new Error('Nothing was queued: this lead was already handled, or you may not have permission.');
  }
}

// ============================================================================
// LISTING ENRICHMENT (forwarded-newsletter digest + candidate triage)
// ============================================================================

/** Candidates queued from a newsletter digest (SELECT-only under RLS). */
export async function fetchListingCandidates(jobId: string): Promise<ListingCandidate[]> {
  const { data, error } = await supabase
    .from('listing_candidates')
    .select('*')
    .eq('ai_import_job_id', jobId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Everything still awaiting a human, across ALL jobs — the review queue's feed.
 *
 * Per-job triage on the import detail page could never keep up: a single weekly
 * "MHC Listing's Rundown" issue queued 187 candidates, and nothing that needs
 * 187 clicks buried inside one job page gets done. Newest listings first.
 */
export async function fetchPendingListingCandidates(limit = 500): Promise<ListingCandidate[]> {
  const { data, error } = await supabase
    .from('listing_candidates')
    .select('*')
    .eq('review_status', 'pending')
    .order('listing_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

// BOTH decisions route through the edge function (RLS grants SELECT only on
// listing_candidates, so a direct update would be denied).
export async function reviewListingCandidate(args: {
  jobId: string;
  candidateId: string;
  decision: 'approved' | 'rejected';
  userName: string;
  userEmail: string;
}) {
  return reviewListingCandidates({ ...args, candidateIds: [args.candidateId] });
}

/**
 * Bulk approve/reject. The edge function already accepted an array of ids —
 * only the client ever sent one at a time, which is what made emptying a
 * 187-row queue impractical.
 */
export async function reviewListingCandidates(args: {
  jobId: string;
  candidateIds: string[];
  decision: 'approved' | 'rejected';
  userName: string;
  userEmail: string;
}) {
  const { data, error } = await supabase.functions.invoke('ai-import-apply', {
    body: {
      job_id: args.jobId,
      action: 'promote_candidates',
      candidate_ids: args.candidateIds,
      decision: args.decision,
      applied_by: args.userName,
      applied_by_email: args.userEmail,
    },
  });
  if (error) throw error;
  return data;
}

export async function undoEnrichmentChanges(args: {
  jobId: string;
  digestIndexes?: number[];
  userEmail: string;
}) {
  const { data, error } = await supabase.functions.invoke('ai-import-undo', {
    body: {
      job_id: args.jobId,
      digest_indexes: args.digestIndexes,
      requested_by_email: args.userEmail,
    },
  });
  if (error) throw error;
  return data;
}

// ============================================================================
// FEEDBACK ADMIN
// ============================================================================

export async function getRecentFeedback(limit = 200): Promise<AIImportFeedback[]> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_feedback?select=*&order=created_at.desc&limit=${limit}`,
    { headers: await restHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch ai_import_feedback: ${response.status}`);
  }
  return response.json();
}

export interface PromptExampleInsert {
  category: string;
  target_field?: string | null;
  target_tool?: string | null;
  example_input: string;
  example_output: unknown;
  example_note?: string | null;
  derived_from_feedback_id?: string | null;
  created_by: string;
  is_active?: boolean;
  priority?: number;
}

export async function promoteFeedbackToExample(insert: PromptExampleInsert): Promise<void> {
  const response = await fetch(`${SUPABASE_URL}/rest/v1/ai_import_prompt_examples`, {
    method: 'POST',
    headers: await restHeaders({ Prefer: 'return=minimal' }),
    body: JSON.stringify({
      is_active: true,
      priority: 0,
      ...insert,
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to promote example: ${response.status} ${text}`);
  }
}

export interface PromptExample {
  id: string;
  category: string;
  target_field: string | null;
  target_tool: string | null;
  example_input: string;
  example_output: unknown;
  example_note: string | null;
  is_active: boolean;
  priority: number;
  effectiveness_score: number | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function getPromptExamples(): Promise<PromptExample[]> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_prompt_examples?select=*&order=created_at.desc`,
    { headers: await restHeaders() },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch prompt examples: ${response.status}`);
  }
  return response.json();
}

export async function toggleExampleActive(id: string, is_active: boolean): Promise<void> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/ai_import_prompt_examples?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: await restHeaders({ Prefer: 'return=minimal' }),
      body: JSON.stringify({ is_active }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to update example: ${response.status}`);
  }
}
